import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { fileTypes, USER_ROLES } from '../../constants';
import useUserDetails from '../hooks/useUserDetails';
import useSchools from '../hooks/useSchools';
import useErrorModal from '../hooks/useErrorModal';
import UploadCard from '../reusable/UploadCard';
import CustomButton from '../reusable/CustomButton';
import Loader from '../reusable/Loader';
import UploadResultsContext from '../../context/UploadResultsContext';
import { UploadedFileData, UploadedFileStatus } from '../../interfaces/UploadedFileDataInterface';
import {
  deleteSjwArtifactPhoto,
  getSjwSchoolPhotoUpload,
  SjwArtifactPhoto,
} from '../../services/sjwPhotoUploadService';
import { postRequestUploadUrl, uploadFileToS3Bucket } from '../../services/uploadService';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import SjwPhotoUploadStatus, { SjwPhotoUploadStatusValue } from './SjwPhotoUploadStatus';
import SjwPhotoUploadCompleteButton from './SjwPhotoUploadCompleteButton';

import './SjwPhotoUploadForSchool.css';

const ARTIFACT_NAMES = [
  'Artifact 3',
  'Artifact 4',
  'Artifact 5',
  'Artifact 6',
  'Artifact 7',
  'Artifact 8',
  'Artifact 9',
] as const;
type ArtifactName = (typeof ARTIFACT_NAMES)[number];

const PHOTO_ACCEPT_TYPES = {
  'image/*': [],
};

type ArtifactFiles = Record<ArtifactName, UploadedFileData[]>;

const emptyArtifactFiles = (): ArtifactFiles =>
  Object.fromEntries(ARTIFACT_NAMES.map((name) => [name, [] as UploadedFileData[]])) as ArtifactFiles;

const buildFileData = (
  artifactName: ArtifactName,
  schoolId: string,
  fileId: string,
  fileName: string,
  status: keyof typeof UploadedFileStatus
): UploadedFileData => ({
  file_id: fileId,
  file_name: fileName,
  upload_file_type: fileTypes.SJW_ARTIFACT_PHOTO,
  status,
  statistics: [],
  results_summary: [],
  missing_columns: [],
  user_provided_file_metadata: { school_id: [schoolId], artifact_name: [artifactName] },
});

const mapPhotoFileStatus = (status: string): UploadedFileStatus => {
  switch (status) {
    case 'UPLOAD_COMPLETE':
      return UploadedFileStatus.SUCCESS;
    case 'UPLOAD_PENDING':
      return UploadedFileStatus.UPLOAD;
    case 'UPLOAD_FAILED':
    default:
      return UploadedFileStatus.UPLOAD_FAILED;
  }
};

const buildArtifactFilesFromResponse = (
  schoolId: string,
  artifacts: { artifact_name: string; photos: SjwArtifactPhoto[] }[]
): ArtifactFiles => {
  const filesByArtifact = emptyArtifactFiles();
  artifacts.forEach(({ artifact_name, photos }) => {
    if ((ARTIFACT_NAMES as readonly string[]).includes(artifact_name)) {
      filesByArtifact[artifact_name as ArtifactName] = photos.map((p) =>
        buildFileData(
          artifact_name as ArtifactName,
          schoolId,
          p.data_file_id,
          p.original_filename ?? '',
          mapPhotoFileStatus(p.file_status)
        )
      );
    }
  });
  return filesByArtifact;
};

const isCompleted = (files: UploadedFileData[]) =>
  files.length > 0 && files.every((f) => f.status === UploadedFileStatus.SUCCESS);
const isUploading = (files: UploadedFileData[]) => files.some((f) => f.status === UploadedFileStatus.UPLOAD);

const getProgressData = (files: UploadedFileData[]) => {
  if (isCompleted(files)) return { state: 'completed' as const, description: 'Uploaded' };
  if (isUploading(files)) return { state: 'progress' as const, description: 'Uploading…' };
  return { state: 'pending' as const, description: 'Not started' };
};

const SjwPhotoUploadForSchool = () => {
  const { schoolId = '' } = useParams<{ schoolId: string }>();
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { schools } = useSchools();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [artifactFiles, setArtifactFiles] = useState<ArtifactFiles>(emptyArtifactFiles);
  const [artifactProgress, setArtifactProgress] = useState<Record<string, Record<string, number>>>({});
  const [workflowStatus, setWorkflowStatus] = useState<SjwPhotoUploadStatusValue>('NOT_STARTED');
  const [isLoading, setIsLoading] = useState(true);

  const school = schools.data?.find((s) => s.id === schoolId);

  useEffect(() => {
    if (!userDetails || !schoolId) return;
    setIsLoading(true);
    getSjwSchoolPhotoUpload(userDetails.eoaId, userDetails.access_token, schoolId)
      .then((response) => {
        setArtifactFiles(buildArtifactFilesFromResponse(schoolId, response.artifacts));
        setWorkflowStatus(response.status);
      })
      .catch((error) => {
        handleOpenErrorModal('Could not load photo upload status.', error as AxiosError);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userDetails, schoolId]);

  const hasAnyFiles = ARTIFACT_NAMES.some((n) => artifactFiles[n].length > 0);
  const overallStatus: SjwPhotoUploadStatusValue =
    workflowStatus === 'COMPLETE' ? 'COMPLETE' : hasAnyFiles ? 'IN_PROGRESS' : workflowStatus;

  const updateArtifactFiles = (
    artifactName: ArtifactName,
    updater: (files: UploadedFileData[]) => UploadedFileData[]
  ) => {
    setArtifactFiles((prev) => ({ ...prev, [artifactName]: updater(prev[artifactName]) }));
  };

  const handleUploadStart = (artifactName: ArtifactName, fileData: UploadedFileData) =>
    updateArtifactFiles(artifactName, (files) => [fileData, ...files]);

  const handleUploadSuccess = (artifactName: ArtifactName, fileData: UploadedFileData) =>
    updateArtifactFiles(artifactName, (files) => files.map((f) => (f.file_id === fileData.file_id ? fileData : f)));

  const handleUploadFailed = (artifactName: ArtifactName, fileId: string) =>
    updateArtifactFiles(artifactName, (files) => files.filter((f) => f.file_id !== fileId));

  const handleDeletePhoto = async (artifactName: ArtifactName, fileId: string) => {
    if (!userDetails) return;
    try {
      await deleteSjwArtifactPhoto(userDetails.eoaId, userDetails.access_token, schoolId, fileId);
      updateArtifactFiles(artifactName, (files) => files.filter((f) => f.file_id !== fileId));
    } catch (error) {
      handleOpenErrorModal('Could not delete photo.', error as AxiosError);
    }
  };

  const clearProgress = (artifactName: ArtifactName, fileId: string) =>
    setArtifactProgress((prev) => {
      const { [fileId]: _, ...rest } = prev[artifactName] ?? {};
      return { ...prev, [artifactName]: rest };
    });

  const startArtifactUpload = async (artifactName: ArtifactName, file: File) => {
    if (!userDetails) return;
    let fileId: string | null = null;
    try {
      const urlResponse = await postRequestUploadUrl(userDetails.eoaId, userDetails.access_token, {
        file_name: file.name,
        user_provided_file_metadata: { school_id: schoolId, artifact_name: artifactName },
        upload_file_type: fileTypes.SJW_ARTIFACT_PHOTO,
      });

      fileId = urlResponse.data.s3_key.split('/').at(-1) ?? '';
      const inProgressFileData = buildFileData(artifactName, schoolId, fileId, file.name, UploadedFileStatus.UPLOAD);
      handleUploadStart(artifactName, inProgressFileData);

      await uploadFileToS3Bucket(urlResponse.data.presigned_url, file, (event) => {
        if (!event.total) return;
        const pct = Math.round((event.loaded * 100) / event.total);
        setArtifactProgress((prev) => ({
          ...prev,
          [artifactName]: { ...(prev[artifactName] ?? {}), [fileId!]: pct },
        }));
      });

      handleUploadSuccess(artifactName, { ...inProgressFileData, status: UploadedFileStatus.SUCCESS });
    } catch (error) {
      if (fileId) handleUploadFailed(artifactName, fileId);
      handleOpenErrorModal('Error occurred while uploading photo!', error as AxiosError);
    } finally {
      if (fileId) clearProgress(artifactName, fileId);
    }
  };

  if (schools.isPending || isLoading) {
    return <Loader />;
  }

  return (
    <div className="main-content">
      <CustomButton
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate(-1)}
      />
      <div className="sjw-photo-upload-header-row">
        <div className="sjw-photo-upload-title-block">
          <h1 className="sjw-photo-upload-title">{school?.name ?? 'School'}</h1>
          <SjwPhotoUploadStatus status={overallStatus} size="small" />
        </div>
        <SjwPhotoUploadCompleteButton schoolId={schoolId} />
      </div>

      <div className="sjw-photo-upload-artifacts">
        {ARTIFACT_NAMES.map((artifactName) => {
          const files = artifactFiles[artifactName];
          return (
            <UploadResultsContext.Provider
              key={artifactName}
              value={{ uploadResults: {}, uploadData: {}, dispatch: () => {} }}
            >
              <UploadCard
                title={artifactName}
                helperText={[]}
                uploadedFilesData={files}
                fileType={fileTypes.SJW_ARTIFACT_PHOTO}
                cardId={`sjw${artifactName.replace(/\s+/g, '')}`}
                acceptFileTypes={PHOTO_ACCEPT_TYPES}
                helperFileTypeText="Please upload image files only"
                uploadProgressData={getProgressData(files)}
                uploadProgressByFileId={artifactProgress[artifactName]}
                allowMultiple
                additionalAllowedRoles={[USER_ROLES.LEAD_FACILITATOR]}
                showAccordion
                showUploadProgress
                onFileSelected={(file) => startArtifactUpload(artifactName, file)}
                onFileDelete={(fileId) => handleDeletePhoto(artifactName, fileId)}
              />
            </UploadResultsContext.Provider>
          );
        })}
      </div>
    </div>
  );
};

export default SjwPhotoUploadForSchool;
